import { List, RefreshCw01, Loading02 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { useTranslation } from "react-i18next";
import type { DeliveryFilter } from "./use-world-map-data";
import { cx } from "@/utils/cx";

export interface WorldMapToolbarProps {
    lastUpdateTime: Date | null;
    formatDateTime: (date: Date) => string;
    isRefreshEnabled: boolean;
    isRefreshingAll: boolean;
    onRefresh: () => void;
    isListPanelOpen: boolean;
    onShowContainers: () => void;
    routesCount: number;
    deliveryFilter: DeliveryFilter;
    onDeliveryFilterChange: (filter: DeliveryFilter) => void;
}

export function WorldMapToolbar({
    lastUpdateTime,
    formatDateTime,
    isRefreshEnabled,
    isRefreshingAll,
    onRefresh,
    isListPanelOpen,
    onShowContainers,
    routesCount,
    deliveryFilter,
    onDeliveryFilterChange,
}: WorldMapToolbarProps) {
    const { t } = useTranslation("pages:containers");

    return (
        <div className="px-6 py-3 border-b border-secondary bg-secondary flex-shrink-0 shadow-xs ring-1 ring-secondary rounded-t-xl flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold text-primary whitespace-nowrap">{t('worldMap.title')}</h1>
            <div className="text-xs text-tertiary bg-primary rounded-lg px-3 py-1.5 ring-1 ring-secondary shadow-xs whitespace-nowrap">
                <span className="text-tertiary">{t('worldMap.lastUpdate')}: </span>
                <span className="text-primary font-medium">
                    {lastUpdateTime ? formatDateTime(lastUpdateTime) : t('worldMap.noContainers', { defaultValue: 'No current containers' })}
                </span>
            </div>
            {isRefreshEnabled && (
                <Tooltip
                    title={t('worldMap.refreshTracking', { defaultValue: 'Refresh Tracking' })}
                    description={t('worldMap.refreshTrackingDesc', { defaultValue: 'Sync latest tracking data from JSONCargo' })}
                >
                    <TooltipTrigger
                        onPress={() => !isRefreshingAll && onRefresh()}
                        isDisabled={isRefreshingAll}
                    >
                        <span className="flex items-center justify-center size-10 rounded-lg bg-primary ring-1 ring-secondary shadow-xs text-tertiary hover:text-primary hover:bg-secondary transition-colors">
                            {isRefreshingAll ? (
                                <Loading02 className="size-5 animate-spin" />
                            ) : (
                                <RefreshCw01 className="size-5" />
                            )}
                        </span>
                    </TooltipTrigger>
                </Tooltip>
            )}
            <div className="flex items-center gap-1 rounded-lg bg-primary p-1 ring-1 ring-secondary">
                <button
                    type="button"
                    onClick={() => onDeliveryFilterChange("in_progress")}
                    className={cx(
                        "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                        deliveryFilter === "in_progress"
                            ? "bg-secondary text-primary shadow-xs"
                            : "text-tertiary hover:text-primary"
                    )}
                >
                    {t('worldMap.filterInProgress', { defaultValue: 'In progress' })}
                </button>
                <button
                    type="button"
                    onClick={() => onDeliveryFilterChange("delivered")}
                    className={cx(
                        "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                        deliveryFilter === "delivered"
                            ? "bg-secondary text-primary shadow-xs"
                            : "text-tertiary hover:text-primary"
                    )}
                >
                    {t('worldMap.filterDelivered', { defaultValue: 'Delivered' })}
                </button>
                <button
                    type="button"
                    onClick={() => onDeliveryFilterChange("all")}
                    className={cx(
                        "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                        deliveryFilter === "all"
                            ? "bg-secondary text-primary shadow-xs"
                            : "text-tertiary hover:text-primary"
                    )}
                >
                    {t('worldMap.filterAll', { defaultValue: 'All' })}
                </button>
            </div>
            {!isListPanelOpen && (
                <Button
                    size="md"
                    color="secondary"
                    iconLeading={List}
                    onClick={onShowContainers}
                >
                    {t('worldMap.showContainers')} ({routesCount})
                </Button>
            )}
        </div>
    );
}
